import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownList } from '../UI/base/DropdownList';
import { PatientBookingType } from '../../api/generated';
import type {
  PatientBookingRequest,
  PatientBookingResponse,
  PatientResponse
} from '../../api/generated';
import {
  BookingFormModalShell,
  BookingOccurrenceFields,
  useBookingFormState
} from '../Bookings/shared';
import formStyles from '../Bookings/shared/BookingFormModal.module.css';
import {
  PATIENT_BOOKING_TYPE,
  PATIENT_BOOKING_TYPE_OPTIONS,
  type PatientBookingTypeOption
} from './patientBookingConstants';

interface PatientBookingFormModalProps {
  booking: PatientBookingResponse | null;
  patients: PatientResponse[];
  onSave: (payload: PatientBookingRequest) => void;
  onClose: () => void;
}

const KEY_PREFIX = 'patient-booking';

export const PatientBookingFormModal = ({
  booking,
  patients,
  onSave,
  onClose
}: PatientBookingFormModalProps) => {
  const { t } = useTranslation();
  const form = useBookingFormState({ booking, keyPrefix: KEY_PREFIX });
  const [bookingType, setBookingType] = useState<PatientBookingType>(
    booking?.patientBookingType ?? PatientBookingType.Specific
  );
  // patientIds is typed as a Set but arrives as a plain JSON array; Array.from handles both.
  const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>(
    booking ? Array.from(booking.patientIds) : []
  );

  const isSpecific = bookingType === PatientBookingType.Specific;

  const handleSave = () => {
    const isValid = form.validate((next) => {
      if (isSpecific && selectedPatientIds.length === 0) {
        next.patients = t('patient-booking-error-patients');
      }
    });
    if (!isValid) return;
    onSave({
      type: PATIENT_BOOKING_TYPE,
      reason: form.reason.trim() || undefined,
      patientBookingType: bookingType,
      // The backend requires patient ids exactly when the type is SPECIFIC — the "all …" modes
      // always resolve against the current patient roster and carry no ids.
      // The Set type is a generated-client artefact; the body is JSON, so we send a plain array.
      patientIds: (isSpecific ? selectedPatientIds : []) as unknown as Set<string>,
      time: form.buildTime()
    });
  };

  return (
    <BookingFormModalShell
      keyPrefix={KEY_PREFIX}
      isEditing={booking !== null}
      onSave={handleSave}
      onClose={onClose}
    >
      <div className={formStyles.field}>
        <span className={formStyles.label}>
          {t('patient-booking-type-label')}
          <span className={formStyles.asterisk}>*</span>
        </span>
        <DropdownList<PatientBookingTypeOption>
          options={PATIENT_BOOKING_TYPE_OPTIONS}
          getKey={(option) => option.value}
          renderLabel={(option) => t(option.labelKey)}
          value={[bookingType]}
          onChange={(next) => setBookingType(next[0] as PatientBookingType)}
        />
      </div>

      {isSpecific && (
        <div className={formStyles.field}>
          <span className={formStyles.label}>
            {t('patient-booking-patients-label')}
            <span className={formStyles.asterisk}>*</span>
          </span>
          <DropdownList<PatientResponse>
            options={patients}
            getKey={(patient) => patient.id}
            renderLabel={(patient) => patient.name}
            selectionMode="multiple"
            value={selectedPatientIds}
            onChange={(next) => setSelectedPatientIds(next as string[])}
            placeholder={t('patient-booking-patients-placeholder')}
          />
          {form.errors.patients && (
            <span className={formStyles.errorText}>{form.errors.patients}</span>
          )}
        </div>
      )}

      <BookingOccurrenceFields form={form} isCreating={booking === null} keyPrefix={KEY_PREFIX} />
    </BookingFormModalShell>
  );
};
